import React, { useCallback, useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useAuth0 } from "@auth0/auth0-react"
import {
  Container, Row, Col, Card, CardBody, Table, Badge, Spinner, Alert, Button,
} from "reactstrap"
import { withTranslation } from "react-i18next"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import { Permissions } from "../../constants/enums"
import { getServiceValidationActions } from "../../helpers/api_rombo_helper"
import { fmtMxDateTime } from "../../helpers/datetime"

// Bitácora TÉCNICA de acciones de un servicio PT (SV2 / PT-4081). Diagnóstico
// operativo: baseline (inline/nats/manual), consolidación diferida, encolado,
// consolidación y fallos de encolado, con su origen (nats/http/taskiq/system) y
// estado (ok/skipped/failed). DISTINTA de la bitácora de negocio (/audit), que
// vive en el Diff. Replica el patrón de página secundaria de ServiceValidationDiff:
// ruta propia /service-validation/:code/actions, permisos de Redux (state.Login),
// authHeader con Auth0, fetch vía la capa api_rombo_helper, y estados
// loading/vacío/error/sin-permiso. La lista viene ordenada del backend (más
// reciente a más antiguo); NO se reordena en cliente.

// Color de Badge por estado. ok=success, skipped=secondary (neutro para
// "omitido"), failed=danger.
const statusColor = (status) =>
  status === "ok" ? "success" : status === "skipped" ? "secondary" : "danger"

// Fila de la tabla con detalle JSON expandible por fila.
const ActionRow = ({ t, action }) => {
  const [open, setOpen] = useState(false)
  const hasDetail = action.detail != null && Object.keys(action.detail || {}).length > 0
  const actionLabel = t(`ActionLog_${action.action}`) !== `ActionLog_${action.action}`
    ? t(`ActionLog_${action.action}`)
    : (action.action || "—")
  const sourceLabel = action.source
    ? (t(`ActionSource_${action.source}`) !== `ActionSource_${action.source}`
      ? t(`ActionSource_${action.source}`)
      : action.source)
    : "—"
  const statusLabel = action.status
    ? (t(`ActionStatus_${action.status}`) !== `ActionStatus_${action.status}`
      ? t(`ActionStatus_${action.status}`)
      : action.status)
    : "—"
  const empresa = action.company_ref || action.partner_ref || "—"
  return (
    <>
      <tr>
        <td className="text-nowrap">{action.created_at ? fmtMxDateTime(action.created_at) : "—"}</td>
        <td className="fw-medium">{actionLabel}</td>
        <td>{sourceLabel}</td>
        <td>
          {action.status
            ? <Badge color={statusColor(action.status)}>{statusLabel}</Badge>
            : "—"}
        </td>
        <td>{action.lot_ref || "—"}</td>
        <td>{empresa}</td>
        <td>{action.created_by || "—"}</td>
        <td>
          {hasDetail ? (
            <Button color="link" size="sm" className="p-0"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={t("ToggleDetail") || "Ver detalle"}>
              <i className={`bx ${open ? "bx-chevron-up" : "bx-chevron-down"} align-middle`} />
              <span className="ms-1">{t("Detail")}</span>
            </Button>
          ) : "—"}
        </td>
      </tr>
      {hasDetail && open && (
        <tr>
          <td colSpan={8} className="bg-light">
            <pre className="mb-0 small">{JSON.stringify(action.detail, null, 2)}</pre>
          </td>
        </tr>
      )}
    </>
  )
}

const ServiceValidationActions = ({ t }) => {
  const { code } = useParams()
  const { getAccessTokenSilently } = useAuth0()
  const [infoUser, setInfoUser] = useState({ permissions: [] })
  const [actions, setActions] = useState([])
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  // true cuando el backend responde 404 (PT-4081 aún no desplegado): se degrada
  // a aviso informativo, NO a error bloqueante.
  const [unavailable, setUnavailable] = useState(false)

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ data: Login.data, loading: Login.loading }),
  )
  const { data, loading } = useSelector(LoginProperties)

  useEffect(() => {
    if (data?.data) setInfoUser(data.data)
  }, [data, loading])

  // Visibilidad de la bitácora técnica y fetch efectivo: ambos usan
  // SERVICE_VALIDATION_READ (consistente con lo que valida el backend).
  const canSeeActions = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_READ)
  const canFetch = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_READ)

  const authHeader = useCallback(async () => {
    const accessToken = await getAccessTokenSilently()
    return { Authorization: `Bearer ${accessToken}` }
  }, [getAccessTokenSilently])

  const loadActions = useCallback(async () => {
    if (!canSeeActions || !canFetch) return
    setLoading(true); setError(null); setUnavailable(false)
    try {
      const token = await authHeader()
      const resp = await getServiceValidationActions(token, code)
      setActions(resp?.actions || [])
    } catch (e) {
      // 404: PT-4081 aún no disponible en backend -> estado informativo, no error.
      if (e?.response?.status === 404) {
        setUnavailable(true)
        setActions([])
      } else {
        setError(e?.response?.data?.detail || t("LoadError"))
        toast.error(e?.response?.data?.detail || t("LoadError"), { autoClose: 3000 })
        setActions([])
      }
    } finally {
      setLoading(false)
    }
  }, [authHeader, canSeeActions, canFetch, code, t])

  useEffect(() => { loadActions() }, [loadActions])

  // Sin permiso de visibilidad: guard de "sin permisos" (igual que Diff).
  if (!canSeeActions) {
    return (
      <div className="page-content">
        <Container fluid>
          <span className="error-message-requests">
            <i className="fa fa-exclamation-circle" /> {t("NoPermissionsViewServices")}
          </span>
        </Container>
      </div>
    )
  }

  return (
    <div className="page-content">
      <ToastContainer position="bottom-right" newestOnTop closeOnClick pauseOnHover />
      <Container fluid>
        <Row className="mb-3">
          <Col xs={12} className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{t("ActionLog")}: {code}</h4>
            <Link to={`/service-validation/${code}`} className="btn btn-sm btn-light">
              {t("Detail")}
            </Link>
          </Col>
        </Row>

        {/* Tiene visibilidad pero el backend rechazaría la llamada (sin READ). */}
        {canSeeActions && !canFetch && (
          <Alert color="warning">{t("ActionLogNeedsReadPermission")}</Alert>
        )}
        {error && <Alert color="danger">{error}</Alert>}

        <Card>
          <CardBody>
            {isLoading ? (
              <div className="text-center py-4"><Spinner color="primary" /></div>
            ) : unavailable ? (
              <Alert color="info" className="mb-0">{t("ActionLogUnavailable")}</Alert>
            ) : actions.length === 0 ? (
              <Alert color="info" className="mb-0">{t("ActionLogEmpty")}</Alert>
            ) : (
              <div className="table-responsive">
                <Table className="table table-bordered mb-0" size="sm">
                  <thead>
                    <tr>
                      <th scope="col">{t("Date")}</th>
                      <th scope="col">{t("Action")}</th>
                      <th scope="col">{t("Source")}</th>
                      <th scope="col">{t("Status")}</th>
                      <th scope="col">{t("Lot")}</th>
                      <th scope="col">{t("Company")}</th>
                      <th scope="col">{t("User")}</th>
                      <th scope="col">{t("Detail")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actions.map((a, i) => (
                      <ActionRow key={`${a.action}-${a.created_at}-${i}`} t={t} action={a} />
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default withTranslation()(ServiceValidationActions)
